import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { getProduct, getPriceAddress, getProductDetailFast } from '../functions/product.js';
import axios from 'axios';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import ClipLoader from 'react-spinners/ClipLoader';
import { Link } from 'react-router-dom';
import { Chip, Typography } from '@material-ui/core';
import { colorsHex, mainColors } from '../utils/colors';
import CustomChip from '../components/CustomChip';
import InfoIcon from '@material-ui/icons/Info';
import SizeChartPopUp from '../components/SizeChartPopUp';
import ProductReviewSection from '../core/reviews/ProductReviewSection';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { isGuestUser, getUserId as getGuestUserId } from '../utils/guestSession';
import StarRatings from 'react-star-ratings';
import './CatalogProduct.css';
import { slugify } from '../utils/slugify';

const CatalogProduct = ({ match }) => {
  const { slug } = useParams();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [priceData, setPriceData] = useState([]);
  const [priceLoader, setPriceLoader] = useState(false);
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [designButtonLoading, setDesignButtonLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const history = useHistory();

  const getDefaultProviderTitle = (providerData = []) => {
    if (!Array.isArray(providerData) || providerData.length === 0) return null;
    const printifyChoice = providerData.find((item) =>
      String(item?.provider_info?.provider_title || "")
        .toLowerCase()
        .includes("printify choice")
    );
    return (
      printifyChoice?.provider_info?.provider_title ||
      providerData?.[0]?.provider_info?.provider_title ||
      null
    );
  };
  
  // Shop design data passed from FilteredProducts (when using design from shop)
  const shopDesignData = location.state?.fromShop ? {
    fromShop: true,
    shopDesignId: location.state.shopDesignId,
    preloadedPrompt: location.state.preloadedPrompt,
    preloadedDesignImage: location.state.preloadedDesignImage,
    designTitle: location.state.designTitle,
  } : null;

  const sizeOrder = ["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL"];

  // Get user ID logic
  const mainData = localStorage.getItem("userData");
  let userId = null;

  // Check if user is guest or logged in
  const isGuest = isGuestUser();
  if (isGuest) {
    // Guest user - use guest session ID
    userId = getGuestUserId();
  } else if (mainData) {
    // Logged in user - use JWT token
    const username = JSON.parse(mainData);
    userId = username?.id_token
      ? jwtDecode(username?.id_token)?.user_id
      : "";
  }

  const fetchStars = async () => {
    try {
      const res = await axios.get(
        `https://printspaceai.com/api/v1/product-reviews/blueprint/avg?blueprint_id=${product?.blueprint_id}`
      );
      setRating(res?.data?.data?.average_rating);
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
    }
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);

        let productData = null;

        // Try fast cached endpoint first, fall back to legacy
        try {
          const fast = await getProductDetailFast(slug);
          if (fast?.data && typeof fast.data === 'object' && fast.data.blueprint_id) {
            productData = fast.data;
          }
        } catch {
          // Fast endpoint unavailable — fall back
        }

        if (!productData) {
          const response = await getProduct(slug);
          const d = response?.data;
          if (d && typeof d === 'object' && !Array.isArray(d) && d.blueprint_id) {
            productData = d;
          }
        }

        if (productData) {
          if (typeof productData.images === 'string') {
            try { productData.images = JSON.parse(productData.images); } catch { productData.images = []; }
          }
          if (!Array.isArray(productData.images)) productData.images = [];
          setProduct(productData);
          setCurrentProvider(getDefaultProviderTitle(productData?.provider_data));
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [slug]);

  useEffect(() => {
    if (product?.blueprint_id) {
      fetchStars();
    }
  }, [product]);

  // Get unique colors for current provider
  let uniqueColors = useMemo(() => {
    return [
      ...new Set(
        product?.provider_data
          ?.find(
            (item) => item?.provider_info?.provider_title === currentProvider
          )
          ?.variant_data?.map((item) => item?.option_color)
      ),
    ];
  }, [currentProvider, product]);

  const visibleColors = showAll ? uniqueColors : uniqueColors.slice(0, 6);

  // Set selected color when colors change
  useEffect(() => {
    setSelectedColor(uniqueColors?.[0]);
  }, [uniqueColors]);

  // Get unique sizes for selected color
  let uniqueSize = useMemo(() => {
    return [
      ...new Set(
        product?.provider_data
          ?.find(
            (item) => item?.provider_info?.provider_title === currentProvider
          )
          ?.variant_data?.filter((item) => item?.option_color === selectedColor)
          ?.map((item) => item?.option_size)
      ),
    ].sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b));
  }, [selectedColor, currentProvider, product]);

  // Set selected size when sizes change
  useEffect(() => {
    setSelectedSize(uniqueSize?.[0]);
  }, [uniqueSize]);

  // Call price API when selections change
  useEffect(() => {
    if (currentProvider && 
        (uniqueColors.length === 0 || selectedColor) && 
        (uniqueSize.length === 0 || selectedSize)) {
      console.log("Fetching product pricing");
      callPriceAddress();
    }
  }, [currentProvider, selectedColor, selectedSize, uniqueColors.length, uniqueSize.length]);

  // Monitor price data changes
  useEffect(() => {
    console.log("Price data updated successfully");
  }, [priceData]);

  const callPriceAddress = async () => {
    const provider_id = product?.provider_data?.find(
      (item) => item?.provider_info?.provider_title === currentProvider
    )?.provider_info?.provider_id;

    // Find the variant based on available options
    let variant_id = null;
    const providerData = product?.provider_data?.find(
      (item) => item?.provider_info?.provider_title === currentProvider
    );
    
    if (providerData?.variant_data) {
      variant_id = providerData.variant_data.find(
        (vitem) => {
          const colorMatch = uniqueColors.length === 0 || vitem.option_color === selectedColor;
          const sizeMatch = uniqueSize.length === 0 || vitem.option_size === selectedSize;
          return colorMatch && sizeMatch;
        }
      )?.variant_id;
    }

    if (!variant_id) return null;

    try {
      setPriceLoader(true);
      const res = await getPriceAddress(
        product?.blueprint_id,
        provider_id,
        variant_id
      );
      const priceDataResult = res?.data;
      setPriceData(priceDataResult);
      return priceDataResult;
    } catch (error) {
      console.error("Error in callPriceAddress:", error);
      return null;
    } finally {
      setPriceLoader(false);
    }
  };

  const getFallbackPriceCents = () => {
    const currentProviderSummary = (product?.providers_summary || []).find((prov) =>
      String(prov?.name || "").toLowerCase() === String(currentProvider || "").toLowerCase()
    );

    const fallbackUsd =
      Number(currentProviderSummary?.price_from) ||
      Number(currentProviderSummary?.lowest_price) ||
      Number(product?.from_usd) ||
      Number((product?.providers_summary || [])[0]?.price_from) ||
      Number((product?.providers_summary || [])[0]?.lowest_price) ||
      0;

    if (!Number.isFinite(fallbackUsd) || fallbackUsd <= 0) return null;
    return Math.round(fallbackUsd * 100);
  };

  const getResolvedProductPriceCents = (pricingData) => {
    const apiPrice = pricingData?.[0]?.first_item?.cost;
    if (Number.isFinite(apiPrice) && apiPrice > 0) return apiPrice;
    return getFallbackPriceCents();
  };

  const handleDesignClick = async () => {
    if (!userId) {
      toast.error("Please login to Apply design");
      return;
    }

    setDesignButtonLoading(true);

    try {
      // Check if price data is available, if not, try to fetch it
      let currentPriceData = priceData;
      if (!currentPriceData || currentPriceData.length === 0 || !currentPriceData[0]?.first_item?.cost) {
        // Show loading state
        toast.info("Loading product pricing...");
        
        // Call the price API to get current pricing
        currentPriceData = await callPriceAddress();
      }

      // If shipping profile is empty for this variant/provider, gracefully use base product price.
      const productPrice = getResolvedProductPriceCents(currentPriceData);
      // Removed detailed price data logging for security
      console.log("Price validation completed", {
        hasPriceData: !!currentPriceData,
        hasShippingProfilePrice: !!(currentPriceData?.[0]?.first_item?.cost),
        isValidPrice: !!(productPrice && productPrice > 0)
      });
      
      if (!productPrice || productPrice <= 0) {
        toast.error("Invalid product pricing. Please try again.");
        return;
      }

      if (!currentPriceData?.[0]?.first_item?.cost) {
        toast.info("Shipping profile unavailable for this variant. Using base product price.");
      }

      // Navigate to designer tool with product data (put price last so product retail `price` does not overwrite shipping/fallback cents).
      const blueprintId = product?.blueprint_id || product?.id || null;
      history.push(`/designertool/${blueprintId}/${slugify(product?.title || "product")}`, {
        ...product,
        selectedSize: selectedSize,
        selectedColor: selectedColor,
        provider_id: product?.provider_data?.find(
          (item) =>
            item?.provider_info?.provider_title === currentProvider
        )?.provider_info?.provider_id,
        variant_id: (() => {
          const providerData = product?.provider_data?.find(
            (item) => item?.provider_info?.provider_title === currentProvider
          );
          if (providerData?.variant_data) {
            const variant = providerData.variant_data.find(
              (vitem) => {
                const colorMatch = uniqueColors.length === 0 || vitem.option_color === selectedColor;
                const sizeMatch = uniqueSize.length === 0 || vitem.option_size === selectedSize;
                return colorMatch && sizeMatch;
              }
            );
            return variant?.variant_id;
          }
          return null;
        })(),
        placeholders: (() => {
          const providerData = product?.provider_data?.find(
            (item) => item?.provider_info?.provider_title === currentProvider
          );
          if (providerData?.variant_data) {
            const variant = providerData.variant_data.find(
              (vitem) => {
                const colorMatch = uniqueColors.length === 0 || vitem.option_color === selectedColor;
                const sizeMatch = uniqueSize.length === 0 || vitem.option_size === selectedSize;
                return colorMatch && sizeMatch;
              }
            );
            return variant?.placeholders;
          }
          return null;
        })(),
        ...(shopDesignData
          ? {
              fromShop: true,
              shopDesignId: shopDesignData.shopDesignId,
              preloadedPrompt: shopDesignData.preloadedPrompt,
              preloadedDesignImage: shopDesignData.preloadedDesignImage,
              designTitle: shopDesignData.designTitle,
            }
          : {}),
        price: productPrice,
        additionalPrice: currentPriceData?.[0]?.additional_item?.cost || 0,
      });
    } catch (error) {
      console.error("Error in handleDesignClick:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setDesignButtonLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="catalog-product-page">
        <div className="container">
          <div className="loading-section">
            <ClipLoader
              color="#007bff"
              loading={loading}
              size={50}
              aria-label="Loading Spinner"
            />
            <p>Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="catalog-product-page">
        <div className="container">
          <div className="error-section">
            <h2>Product Not Found</h2>
            <p>{error || 'The requested product could not be found.'}</p>
            <button 
              onClick={() => history.goBack()}
              className="back-btn"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="catalog-product-page">
      <div className="container">
        <div className="back-link" onClick={() => history.goBack()}>
          ← Back to products
        </div>

        {/* Shop Design Banner */}
        {shopDesignData && (
          <div style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #2196f3",
            borderRadius: "8px",
            padding: "16px 20px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}>
            {shopDesignData.preloadedDesignImage && (
              <img 
                src={shopDesignData.preloadedDesignImage} 
                alt="Design" 
                style={{ 
                  width: "60px", 
                  height: "60px", 
                  objectFit: "contain",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  backgroundColor: "#fff"
                }}
              />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "600", color: "#1565c0", marginBottom: "4px" }}>
                Using Design from Shop
              </div>
              <div style={{ fontSize: "14px", color: "#555" }}>
                {shopDesignData.designTitle || "Custom Design"} - Select size and color below, then click "Start Designing"
              </div>
            </div>
          </div>
        )}

        <div className="product-layout">
          <div className="product-images">
            {product.images && product.images.length > 0 ? (
              <Carousel 
                showArrows={true} 
                showThumbs={true}
                infiniteLoop
                autoPlay
                interval={5000}
              >
                {product.images.map((image, index) => (
                  <div key={index}>
                    <img 
                      src={image} 
                      alt={`${product.title} - Image ${index + 1}`}
                      className="product-image"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ))}
              </Carousel>
            ) : (
              <div className="no-image">
                <span>📦</span>
                <p>No image available</p>
              </div>
            )}

            {/* Conversion-focused controls under image (desktop + mobile) */}
            <div className="conversion-panel">
              {/* Provider Selection */}
              {product?.provider_data?.length > 0 && (
                <div className="provider-section">
                  <h3>Providers:</h3>
                  <div className="provider-options">
                    {product?.provider_data?.map((item, index) => (
                      <div
                        key={index}
                        className="provider-option"
                        onClick={() =>
                          setCurrentProvider(
                            item?.provider_info?.provider_title
                          )
                        }
                      >
                        <CustomChip
                          currentProvider={currentProvider}
                          providerTitle={item?.provider_info?.provider_title}
                          providerRating={item.provider_info.provider_rating}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Selection */}
              {visibleColors?.[0] && (
                <div className="color-section">
                  <h3>Colors:</h3>
                  <div className="color-options">
                    {visibleColors?.map((item, index) => {
                      const lowerItem = item?.toLowerCase();

                      let newColor = colorsHex?.find(
                        (c) => c?.name?.toLowerCase() === lowerItem
                      );

                      if (!newColor) {
                        const itemWords = lowerItem.split(/[\s\-_,]+/);
                        const fallbackColor = mainColors?.find((c) =>
                          itemWords.includes(c.name.toLowerCase())
                        );
                        if (fallbackColor) {
                          newColor = fallbackColor;
                        }
                      }

                      return (
                        <div
                          key={index}
                          className="color-option"
                          onClick={() => setSelectedColor(item)}
                        >
                          <Chip
                            label={
                              <div className="color-chip-content">
                                <span
                                  className="color-indicator"
                                  style={{
                                    backgroundColor: newColor?.hex || "#CCCCCC",
                                  }}
                                />
                                {item}
                              </div>
                            }
                            className={`color-chip ${selectedColor === item ? 'selected' : ''}`}
                          />
                        </div>
                      );
                    })}

                    {uniqueColors.length > 8 && (
                      <div
                        className="show-more-colors"
                        onClick={() => setShowAll(!showAll)}
                      >
                        {showAll ? "Show Less" : "Show More"}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Size Selection */}
              {uniqueSize?.length > 0 && (
                <div className="size-section">
                  <h3>Size:</h3>
                  <div className="size-options">
                    {uniqueSize.map((item, index) => (
                      <div
                        key={index}
                        className="size-option"
                        onClick={() => setSelectedSize(item)}
                      >
                        <Chip
                          label={item}
                          className={`size-chip ${selectedSize === item ? 'selected' : ''}`}
                        />
                      </div>
                    ))}

                    {Object.keys(product?.measurments || {}).length > 0 && (
                      <div
                        className="size-chart-link"
                        onClick={() => setSizeChartOpen(true)}
                      >
                        <InfoIcon className="info-icon" />
                        <span>Size chart</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notice if no variant data available yet */}
              {(!product?.provider_data || product.provider_data.length === 0) && (
                <div className="variant-warning">
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#e65100" }}>
                    Colors & sizes are not yet available for this product
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    This product's variants are being synced. Please check back shortly.
                  </Typography>
                </div>
              )}

              <div className="product-actions">
                <button 
                  onClick={handleDesignClick}
                  className="design-btn"
                  disabled={!currentProvider || designButtonLoading}
                >
                  {designButtonLoading ? "Loading..." : "Start Designing"}
                </button>
                
                <Link to="/products" className="browse-btn">
                  Browse More Products
                </Link>
              </div>
            </div>
          </div>

          <div className="product-details">
            <h1 className="product-title">{product.title}</h1>
            
            {/* Product Rating */}
            {rating ? (
              <div className="product-rating">
                <StarRatings
                  rating={rating}
                  starRatedColor="#ffd700"
                  numberOfStars={5}
                  name="rating"
                  starDimension="20px"
                  starSpacing="3px"
                />
                <span className="rating-text">({rating.toFixed(1)} out of 5)</span>
              </div>
            ) : null}
            
            <div className="product-meta">
              <p className="product-brand">Brand: {product.brand}</p>
              {product.model && (
                <p className="product-model">Model: {product.model}</p>
              )}
            </div>

            {/* Enriched Product Details (placed above description to match product page flow) */}
            {(Array.isArray(product.key_features) && product.key_features.length > 0) && (
              <div className="product-description">
                <h3>Key Features</h3>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {product.key_features.map((f, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>{f}</li>
                  ))}
                </ul>
              </div>
            )}

            {product.care_instructions && (
              <div className="product-description">
                <h3>Care Instructions</h3>
                <div className="description-content">
                  {product.care_instructions}
                </div>
              </div>
            )}

            {product.size_guide && Array.isArray(product.size_guide.headers) && product.size_guide.headers.length > 0 && (
              <div className="product-description">
                <h3>Size Guide</h3>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                    <thead>
                      <tr>
                        {product.size_guide.headers.map((h, i) => (
                          <th key={i} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #ddd" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(product.size_guide.rows || []).map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci} style={{ padding: "7px 12px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {Array.isArray(product.providers_summary) && product.providers_summary.length > 0 && (
              <div className="product-description">
                <h3>Provider Details</h3>
                <div className="description-content">
                  {product.providers_summary.map((prov, i) => (
                    <div key={i} style={{ marginBottom: 10 }}>
                      {(() => {
                        const priceVal =
                          prov.price_from != null ? prov.price_from : prov.lowest_price;
                        const shippingVal =
                          prov.shipping_from != null ? prov.shipping_from : prov.shipping_from_us;
                        const productionVal =
                          prov.avg_production_days ||
                          (typeof prov.production_text === "string"
                            ? (prov.production_text.match(/([\d]+\.?[\d]*)\s*days?/i)?.[0] || "")
                            : "");
                        const sizesVal =
                          prov.sizes != null
                            ? `${prov.sizes}${prov.size_range ? ` (${prov.size_range})` : ""}`
                            : "";
                        const colorsVal = prov.colors != null ? prov.colors : "";

                        return (
                          <>
                            <strong>{prov.name || "Provider"}</strong>
                            {prov.location ? ` • ${prov.location}` : ""}
                            {priceVal != null ? ` • From USD ${priceVal}` : ""}
                            {shippingVal != null ? ` • Shipping USD ${shippingVal}` : ""}
                            {productionVal ? ` • Avg production ${productionVal}` : ""}
                            {sizesVal ? ` • Sizes ${sizesVal}` : ""}
                            {colorsVal !== "" ? ` • Colors ${colorsVal}` : ""}
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="product-description">
              <h3>Description</h3>
              <div 
                className="description-content"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </div>

          </div>
        </div>
      </div>

      {/* Product Reviews Section */}
      <div className="reviews-section">
        <ProductReviewSection blueprintId={product?.blueprint_id} />
      </div>

      {/* Size Chart Popup */}
      {product?.measurments && (
        <SizeChartPopUp
          open={sizeChartOpen}
          setOpen={setSizeChartOpen}
          sizeData={product?.measurments}
        />
      )}
    </div>
  );
};

export default CatalogProduct;
